// src/components/manager/ManagerRoutesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, CardTitle, Row, Col,
    Table, Alert, Spinner, Input, Button, Modal, ModalHeader, ModalBody, FormGroup, Label
} from "reactstrap";
import AuthFunctions from '../../services/AuthService';
import Session from '../../auth/Session';
import { useNavigate } from 'react-router-dom';
import * as dayjs from 'dayjs';
import SearchableDropdownFilter from "../SearchableDropdownFilter";
import { getFilters, saveFilter } from "../filterStorage";
import { WT_ADT_ENABLED } from "../../../featureFlags";

const getCustomerLabel = (customer) => customer.name;
const getSubCustomerLabel = (subCustomer) =>
    `${subCustomer.name}${subCustomer.customer ? ` — ${subCustomer.customer.name}` : ""}`;
const getOptionId = (option) => option.id;
const normalizeState = (value) => String(value || "").trim().toUpperCase();

const getDriverStates = (driver) => {
    const states = [
        ...(driver?.states || []),
        ...(driver?.stateAssignments || []).map(a => a.state),
    ];

    if (!states.length && driver?.state) {
        states.push(driver.state);
    }

    return [...new Set(states.map(normalizeState).filter(Boolean))];
};

const driverMatchesState = (driver, state) =>
    getDriverStates(driver).includes(normalizeState(state));

const ManagerDailyRoutes = () => {
    const navigate = useNavigate();
    const apiUrl = "/api/manager/daily-route-schedules";
    //const apiScheduleDateUrl = "/api/manager/routeschedules-and-schedule-for-date";
    const routesApi = "/api/routes";
    const routesUploadApi = "/api/routes/upload-week";
    const routesUploadValidateApi = "/api/routes/upload-week/validate";
    const routeInfoApi = "/api/routes/route-info";
    const createChildUrl = "/api/routedailyschedules";
    const driversApi = "/api/drivers/list";
    const subCustomersApi = "/api/subcustomers";
    const customersApi = "/api/customers";
    const routeWeeksApi = "/api/routes/weeks";
    const patchDaysUrl = (id) => `/api/routedailyschedules/${id}/days`;
    const patchMileageUrl = (id) => `/api/routedailyschedules/${id}/mileage`;
    const patchStartTimeUrl = (id) => `/api/routedailyschedules/${id}/starttime`;
    const patchEndTimeUrl = (id) => `/api/routedailyschedules/${id}/endtime`;
    const patchCommentsUrl = (id) => `/api/routedailyschedules/${id}/comments`;
    const patchInvoicingCommentsUrl = (id) => `/api/routedailyschedules/${id}/invoicing-comments`;
    const patchAdtUrl = (id) => `/api/routedailyschedules/${id}/adt`;
    const patchDriverUrl = (id, driverId) => `/api/routedailyschedules/${id}/driver/${driverId}`;
    const deleteSplitUrl = (id) => `/api/routedailyschedules/${id}/split`;

    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [routeSchedules, setRouteSchedules] = useState([]);
    const [filteredRoutes, setFilteredRoutes] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [subCustomers, setSubCustomers] = useState([]);
    const [error, setError] = useState("");
    const [customerId, setCustomerId] = useState(() => getFilters().customerId || "");
    const [subCustomerId, setSubCustomerId] = useState(() => getFilters().subCustomerId || "");
    // Add-child modal state
    const [addOpen, setAddOpen] = useState(false);
    const [addForRouteId, setAddForRouteId] = useState(null);
    const [addForState, setAddForState] = useState(null);
    const [newDriverId, setNewDriverId] = useState("");
    const [newDays, setNewDays] = useState(0);
    const [newMileage, setNewMileage] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadIssues, setUploadIssues] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingPreview, setPendingPreview] = useState(null);
    const [suspectDecisions, setSuspectDecisions] = useState({});
    // Keyed by ambiguous driver name -> selected driver id (as a string).
    const [driverSelections, setDriverSelections] = useState({});
    const [loadingDeviceRows, setLoadingDeviceRows] = useState({});
    const [spireonPreviewOpen, setSpireonPreviewOpen] = useState(false);
    const [spireonPreviewRoute, setSpireonPreviewRoute] = useState(null);
    const [spireonPreviewSegments, setSpireonPreviewSegments] = useState([]);
    const [mergeSpireonSplits, setMergeSpireonSplits] = useState(false);

    const [weeks, setWeeks] = useState([
        { endingDate: '', displayDate: '' }
    ]);
    const [routeDays, setRouteDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState('');
    const [selectedWeek, setSelectedWeek] = useState('');
    const [file, setFile] = useState(null);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesRowId, setNotesRowId] = useState(null);
    const [notesText, setNotesText] = useState("");
    const [notesSaving, setNotesSaving] = useState(false);
    // Invoicing notes (new import format only): separate note that impacts the customer invoice.
    const [invNotesOpen, setInvNotesOpen] = useState(false);
    const [invNotesRowId, setInvNotesRowId] = useState(null);
    const [invNotesText, setInvNotesText] = useState("");
    const [invNotesSaving, setInvNotesSaving] = useState(false);

    const getAssignedSubCustomers = (routes) => {
        const map = new Map();
        for (const rs of routes || []) {
            const subCustomer = rs?.route?.subCustomer;
            if (subCustomer?.id && !map.has(subCustomer.id)) {
                map.set(subCustomer.id, subCustomer);
            }
        }

        return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    };

    const buildRouteUrl = (selectedDate, selectedCustomerId = customerId, selectedSubCustomerId = subCustomerId) => {
        const params = new URLSearchParams();
        if (selectedDate) params.set('dateTime', selectedDate);
        if (isAdmin) {
            params.set('customerId', selectedCustomerId);
            params.set('subCustomerId', selectedSubCustomerId);
        }
        const query = params.toString();
        return query ? `${apiUrl}?${query}` : apiUrl;
    };

    const clearRouteData = () => {
        setSchedule(null);
        setRouteSchedules([]);
        setFilteredRoutes([]);
    };

    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) {
                navigate('/logout');
            }
            else {
                const token = await Session.getToken();
                const adminUser = await AuthFunctions.getIsAdminAsync();
                setIsAdmin(adminUser);

                const commonRequests = [
                    fetch(driversApi, { headers: { Authorization: `Bearer ${token}` } }),
                    fetch(routeWeeksApi, { headers: { Authorization: `Bearer ${token}` } })
                ];

                if (adminUser) {
                    commonRequests.push(fetch(customersApi, { headers: { Authorization: `Bearer ${token}` } }));
                    commonRequests.push(fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } }));
                } else {
                    commonRequests.push(fetch(apiUrl, { headers: { Authorization: `Bearer ${token}` } }));
                }

                const responses = await Promise.all(commonRequests);
                const [dRes, rwRes] = responses;
                setDrivers(dRes.ok ? await dRes.json() : []);
                setWeeks(rwRes.ok ? await rwRes.json() : []);

                if (adminUser) {
                    const cRes = responses[2];
                    const scRes = responses[3];
                    setCustomers(cRes.ok ? await cRes.json() : []);
                    setSubCustomers(scRes.ok ? await scRes.json() : []);
                    clearRouteData();
                } else {
                    const res = responses[2];
                    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                    const data = await res.json();
                    const routes = data.routeSchedules || [];
                    setSchedule(data.schedule);
                    setRouteSchedules(routes);
                    setSubCustomers(getAssignedSubCustomers(routes));
                }
            }
        } catch (e) {
            setError(e.message || "Failed to load manager data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const stored = getFilters();
        const match = stored.selectedWeek ? weeks.find(w => w.endingDate === stored.selectedWeek) : null;
        const selected = match || weeks.find(w => w.selected === true);
        if (!selected) return;

        setSelectedWeek(selected.endingDate);
        setRouteDays(selected.routeDays);

        const currentDay = dayjs().format('MM/DD/YYYY');
        const day = stored.selectedDay && selected.routeDays.includes(stored.selectedDay)
            ? stored.selectedDay
            : selected.routeDays.includes(currentDay)
                ? currentDay
                : selected.routeDays[0];
        setSelectedDay(day);

        if (isAdmin && stored.customerId && stored.subCustomerId) {
            fetchScheduleForDate(day, stored.customerId, stored.subCustomerId);
        }
    }, [weeks]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadData(); }, []);

    const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");

    // Group by (scheduleId, routeId)
    const grouped = useMemo(() => {
        const m = new Map();
        for (const rs of filteredRoutes) {
            const key = `${rs.scheduleId}|${rs.routeId}`;
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(rs);
        }
        return Array.from(m.values()).map(list => {
            list.sort((a, b) => a.id - b.id);
            return { parent: list[0], children: list.slice(1) };
        });
    }, [filteredRoutes]);

    const availableSubCustomers = useMemo(() => {
        if (!isAdmin || customerId === '') return subCustomers;
        return subCustomers.filter(sc => `${sc.customerId}` === `${customerId}`);
    }, [isAdmin, subCustomers, customerId]);

    useEffect(() => {
        let filtered = [];
        if (isAdmin && subCustomerId === '') {
            filtered = [];
        } else if (subCustomerId !== '') {
            filtered = routeSchedules.filter(rs => `${rs?.route?.subCustomerId}` === subCustomerId);
        } else {
            filtered = [...routeSchedules];
        }
        setFilteredRoutes(filtered);
    }, [routeSchedules, subCustomerId, isAdmin]);

    // === Inline day edit ===
    const updateLocal = (id, patch) =>
        setRouteSchedules(prev => prev.map(rs => (rs.id === id ? { ...rs, ...patch } : rs)));

    const saveDays = async (id, value, revert) => {
        try {
            const token = await Session.getToken();
            const res = await fetch(patchDaysUrl(id), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ DaysOnRoute: value }),
            });
            if (!res.ok) {
                updateLocal(id, { daysOnRoute: revert });
                throw new Error(await res.text() || "Failed to save");
            }
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        }
    };

    const saveMileage = async (id, value, revert, startMileage = null, endMileage = null) => {
        try {
            const token = await Session.getToken();
            const body = { Mileage: value };
            if (startMileage !== null && startMileage !== undefined) body.StartMileage = startMileage;
            if (endMileage !== null && endMileage !== undefined) body.EndMileage = endMileage;
            const res = await fetch(patchMileageUrl(id), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                updateLocal(id, { mileage: revert });
                throw new Error(await res.text() || "Failed to save");
            }
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        }
    };

    const openNotes = (rs) => {
        setNotesRowId(rs.id);
        setNotesText(rs.comments ?? "");
        setNotesOpen(true);
    };

    const saveNotes = async () => {
        if (notesRowId == null) return;
        setNotesSaving(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(patchCommentsUrl(notesRowId), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ Comments: notesText }),
            });
            if (!res.ok) throw new Error(await res.text() || "Failed to save");
            updateLocal(notesRowId, { comments: notesText.trim() === "" ? null : notesText.trim() });
            setNotesOpen(false);
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        } finally {
            setNotesSaving(false);
        }
    };

    const openInvNotes = (rs) => {
        setInvNotesRowId(rs.id);
        setInvNotesText(rs.invoicingComments ?? "");
        setInvNotesOpen(true);
    };

    const saveInvNotes = async () => {
        if (invNotesRowId == null) return;
        setInvNotesSaving(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(patchInvoicingCommentsUrl(invNotesRowId), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ InvoicingComments: invNotesText }),
            });
            if (!res.ok) throw new Error(await res.text() || "Failed to save");
            updateLocal(invNotesRowId, { invoicingComments: invNotesText.trim() === "" ? null : invNotesText.trim() });
            setInvNotesOpen(false);
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        } finally {
            setInvNotesSaving(false);
        }
    };

    const saveStart = async (id, value, revert) => {
        try {
            const token = await Session.getToken();
            const res = await fetch(patchStartTimeUrl(id), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ StartTime: value }),
            });
            if (!res.ok) {
                updateLocal(id, { startTime: revert });
                throw new Error(await res.text() || "Failed to save");
            }
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        }
    };

    const saveEnd = async (id, value, revert) => {
        try {
            const token = await Session.getToken();
            const res = await fetch(patchEndTimeUrl(id), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ EndTime: value }),
            });
            if (!res.ok) {
                updateLocal(id, { endTime: revert });
                throw new Error(await res.text() || "Failed to save");
            }
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        }
    };
    const handleDays = (id, e, prev) => {
        const val = e.target.value;
        updateLocal(id, { daysOnRoute: val });
        if (e.type === "blur" || e.key === "Enter") {
            const num = val === "" ? 0 : Number(val);
            if (isNaN(num) || num < 0) return updateLocal(id, { daysOnRoute: prev });
            if (num !== prev) saveDays(id, num, prev);
        }
        if (e.key === "Escape") updateLocal(id, { daysOnRoute: prev });
    };

    const handleStart = (id, e, prev) => {
        const val = e.target.value;
        updateLocal(id, { startTime: val });
        if (e.type === "blur" || e.key === "Enter") {
            const current = val === "" ? "" : val;
            saveStart(id, current, prev);
        }
        if (e.key === "Escape") updateLocal(id, { startTime: prev });
    };

    const handleEnd = (id, e, prev) => {
        const val = e.target.value;
        updateLocal(id, { endTime: val });
        if (e.type === "blur" || e.key === "Enter") {
            const current = val === "" ? "" : val;
            saveEnd(id, current, prev);
        }
        if (e.key === "Escape") updateLocal(id, { endTime: prev });
    };

    // Payable ADT override in hours. A null value (empty input) clears the override so ADT
    // reverts to the auto-calculation.
    const saveAdt = async (id, value, revert) => {
        try {
            const token = await Session.getToken();
            const res = await fetch(patchAdtUrl(id), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({ OvertimeHoursOverride: value }),
            });
            if (!res.ok) {
                updateLocal(id, { overtimeHoursOverride: revert });
                throw new Error(await res.text() || "Failed to save");
            }
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        }
    };

    const handleAdt = (id, e, prev) => {
        const val = e.target.value;
        updateLocal(id, { overtimeHoursOverride: val });
        if (e.type === "blur" || e.key === "Enter") {
            const num = val === "" ? null : Number(val);
            if (num !== null && (isNaN(num) || num < 0)) return updateLocal(id, { overtimeHoursOverride: prev });
            saveAdt(id, num, prev);
        }
        if (e.key === "Escape") updateLocal(id, { overtimeHoursOverride: prev });
    };

    const handleMileage = (id, e, prev) => {
        const val = e.target.value;
        updateLocal(id, { mileage: val });
        if (e.type === "blur" || e.key === "Enter") {
            const num = val === "" ? 0 : Number(val);
            if (isNaN(num) || num < 0) return updateLocal(id, { mileage: prev });
            if (num !== prev) saveMileage(id, num, prev);
        }
        if (e.key === "Escape") updateLocal(id, { mileage: prev });
    };

    // === Driver edit ===
    const saveDriver = async (id, driverId, revert) => {
        try {
            const token = await Session.getToken();
            const res = await fetch(patchDriverUrl(id, driverId), {
                method: "PATCH",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }
            });
            if (!res.ok) {
                updateLocal(id, { driverId: revert });
                throw new Error(await res.text() || "Failed to save driver");
            }
            const updated = await res.json();
            updateLocal(id, { driverId: updated.driverId, driver: updated.driver });
        } catch (e) {
            setError(e.message);
            setTimeout(() => setError(""), 4000);
        }
    };

    const handleDriverChange = (id, e, prev) => {
        const newVal = Number(e.target.value);
        updateLocal(id, { driverId: newVal });
        if (newVal !== prev) saveDriver(id, newVal, prev);
    };

    const toMinutes = (time) => {
        const parts = String(time || "").split(':');
        if (parts.length < 2) return null;
        const hours = Number(parts[0]);
        const minutes = Number(parts[1]);
        if (isNaN(hours) || isNaN(minutes)) return null;
        return hours * 60 + minutes;
    };

    const getSelectedSpireonSegments = () =>
        spireonPreviewSegments
            .filter(segment => segment.selected)
            .sort((a, b) => (a.segmentIndex ?? 0) - (b.segmentIndex ?? 0));

    const mergeSelectedSpireonSegments = (segments) => {
        const ordered = [...segments].sort((a, b) => {
            const startA = toMinutes(a.startTime);
            const startB = toMinutes(b.startTime);
            if (startA === null && startB === null) return (a.segmentIndex ?? 0) - (b.segmentIndex ?? 0);
            if (startA === null) return 1;
            if (startB === null) return -1;
            return startA - startB;
        });
        const first = ordered[0] || {};
        const last = [...ordered].reverse().find(segment => segment.endTime) || ordered[ordered.length - 1] || {};
        const startMileage = first.startMileage ?? null;
        const endMileage = last.endMileage ?? null;
        const calculatedMileage = startMileage !== null && endMileage !== null && endMileage >= startMileage
            ? endMileage - startMileage
            : ordered.reduce((sum, segment) => sum + (Number(segment?.mileage) || 0), 0);

        return {
            ...first,
            segmentIndex: 0,
            daysOnRoute: 1,
            mileage: calculatedMileage,
            startTime: first.startTime || "",
            endTime: last.endTime || "",
            startMileage,
            endMileage,
        };
    };

    const getPendingSpireonRows = () => {
        const selected = getSelectedSpireonSegments();
        if (!selected.length) return [];
        return mergeSpireonSplits ? [mergeSelectedSpireonSegments(selected)] : selected;
    };

    const updateSpireonSegmentSelection = (segmentIndex, selected) => {
        setSpireonPreviewSegments(prev => {
            const next = prev.map(segment =>
                segment.segmentIndex === segmentIndex ? { ...segment, selected } : segment
            );
            if (next.filter(segment => segment.selected).length < 2) {
                setMergeSpireonSplits(false);
            }
            return next;
        });
    };

    const buildSpireonDailyPayload = (segment, routeId, driverId = 0) => ({
        scheduleId: schedule.id,
        routeDate: dayjs(selectedDay, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        routeId,
        driverId: driverId || 0,
        daysOnRoute: Number(segment?.daysOnRoute) || 0,
        mileage: Math.round(Number(segment?.mileage) || 0),
        startTime: segment?.startTime || "",
        endTime: segment?.endTime || "",
        startMileage: segment?.startMileage ?? null,
        endMileage: segment?.endMileage ?? null,
    });

    const applySpireonSegmentToRow = async (row, segment) => {
        const daysOnRoute = Number(segment?.daysOnRoute) || 0;
        const mileage = Math.round(Number(segment?.mileage) || 0);
        const startTime = segment?.startTime || "";
        const endTime = segment?.endTime || "";
        const startMileage = segment?.startMileage ?? null;
        const endMileage = segment?.endMileage ?? null;

        updateLocal(row.id, {
            daysOnRoute,
            mileage,
            startTime,
            endTime,
            startMileage,
            endMileage,
        });

        await saveDays(row.id, daysOnRoute, row.daysOnRoute);
        await saveMileage(row.id, mileage, row.mileage, startMileage, endMileage);
        await saveStart(row.id, startTime, row.startTime);
        await saveEnd(row.id, endTime, row.endTime);
    };

    const createSpireonSplitRow = async (segment, routeId) => {
        const token = await Session.getToken();
        const res = await fetch(createChildUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify(buildSpireonDailyPayload(segment, routeId)),
        });
        if (!res.ok) throw new Error(await res.text() || "Create failed");
        const created = await res.json();
        setRouteSchedules(prev => [...prev, created]);
        return created;
    };

    const deleteSplitRow = async (id) => {
        try {
            const token = await Session.getToken();
            const res = await fetch(deleteSplitUrl(id), {
                method: "DELETE",
                headers: { "Authorization": `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(await res.text() || "Delete failed");
            setRouteSchedules(prev => prev.filter(rs => rs.id !== id));
        } catch (e) {
            setError(e.message || "Delete failed");
            setTimeout(() => setError(""), 4000);
        }
    };

    // === Add child route schedule ===
    const openAdd = (routeId, state) => {
        setAddForRouteId(routeId);
        setAddForState(state);
        setNewDriverId("");
        setNewDays(0);
        setNewMileage(0);
        setAddOpen(true);
    };

    const loadDataFromDevice = async (rs, routeId) => {
        const rowKey = rs.id;
        if (loadingDeviceRows[rowKey]) return;

        setError("");
        setLoadingDeviceRows(prev => ({ ...prev, [rowKey]: true }));
        try {
            var startDate = selectedDay;
            var date = new Date(selectedDay);
            date.setDate(date.getDate() + 1);
            var endDate = date.toISOString().split('T')[0];

            const token = await Session.getToken();
            const params = new URLSearchParams({
                startDate,
                endDate,
                routeId,
            });
            const res = await fetch(`${routeInfoApi}?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) {
                throw new Error(await res.text() || "Failed to load Spireon data");
            }

            let data = await res.json();
            if (data && data.length) {
                setSpireonPreviewRoute({ ...rs, routeId });
                const mapped = data.map((segment, index) => ({
                    ...segment,
                    segmentIndex: segment.segmentIndex ?? index,
                    // Default to unselected when the parser flagged the segment for exclusion.
                    selected: !segment.excludeFromBilling,
                }));
                setSpireonPreviewSegments(mapped);
                // Auto-enable merge when the parser found UNAUTH_MOV-split segments.
                const suggestMerge = mapped.length > 1 && mapped.some(s => s.canMergeWithNext);
                setMergeSpireonSplits(suggestMerge);
                setSpireonPreviewOpen(true);
            } else {
                setError("No Spireon data was found for this route and date.");
                setTimeout(() => setError(""), 4000);
            }
        } catch (e) {
            setError(e.message || "Failed to load Spireon data");
            setTimeout(() => setError(""), 4000);
        } finally {
            setLoadingDeviceRows(prev => {
                const next = { ...prev };
                delete next[rowKey];
                return next;
            });
        }
    };

    const cancelSpireonPreview = () => {
        setSpireonPreviewOpen(false);
        setSpireonPreviewRoute(null);
        setSpireonPreviewSegments([]);
        setMergeSpireonSplits(false);
    };

    const applySpireonPreview = async () => {
        const routeContext = spireonPreviewRoute;
        const selectedRows = getPendingSpireonRows();
        if (!routeContext || selectedRows.length === 0) return;

        try {
            const existingRows = routeSchedules
                .filter(row => row.scheduleId === routeContext.scheduleId && row.routeId === routeContext.routeId)
                .sort((a, b) => a.id - b.id);

            for (let i = 0; i < selectedRows.length; i++) {
                const segment = selectedRows[i];
                const existingRow = existingRows[i];
                if (existingRow) {
                    await applySpireonSegmentToRow(existingRow, segment);
                } else {
                    const created = await createSpireonSplitRow(segment, routeContext.routeId);
                    existingRows.push(created);
                }
            }

            for (let i = selectedRows.length; i < existingRows.length; i++) {
                await deleteSplitRow(existingRows[i].id);
            }

            cancelSpireonPreview();
        } catch (e) {
            setError(e.message || "Failed to apply Spireon data");
            setTimeout(() => setError(""), 4000);
        }
    };

    const submitAddChild = async () => {
        if (!schedule?.id || !addForRouteId || !newDriverId) return;
        try {
            const token = await Session.getToken();
            const res = await fetch(createChildUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({
                    scheduleId: schedule.id,
                    routeDate: dayjs(selectedDay, 'MM/DD/YYYY').format('YYYY-MM-DD'),
                    routeId: addForRouteId,
                    driverId: Number(newDriverId),
                    daysOnRoute: Number(newDays) || 0,
                    mileage: Number(newMileage) || 0,
                }),
            });
            if (!res.ok) throw new Error(await res.text() || "Create failed");
            const created = await res.json();
            setRouteSchedules(prev => [...prev, created]);
            setAddOpen(false);
        } catch (e) {
            setError(e.message || "Create failed");
        }
    };

    const fetchScheduleForDate = async (selectedDay, selectedCustomerId = customerId, selectedSubCustomerId = subCustomerId) => {
        if (isAdmin && (!selectedCustomerId || !selectedSubCustomerId)) {
            clearRouteData();
            return;
        }

        const token = await Session.getToken();
        const res = await fetch(buildRouteUrl(selectedDay, selectedCustomerId, selectedSubCustomerId), { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
            let data = await res.json();
            setSchedule(data.schedule);
            setRouteSchedules(data.routeSchedules || []);
        }
    }

    const sumDays = (parent, children) => {
        const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
        return [parent, ...children].reduce((acc, rs) => acc + toNum(rs.daysOnRoute), 0);
    };


    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploadIssues(null);
        setPendingPreview(null);
        setSuspectDecisions({});
        setDriverSelections({});
        try {
            setUploading(true);
            const token = await Session.getToken();
            const res = await fetch(routesUploadValidateApi, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            if (!res.ok) {
                throw new Error(await res.text() || "Validation failed");
            }
            const preview = await res.json();
            if (!preview.isValid) {
                setUploadIssues(preview);
                return;
            }
            setPendingPreview(preview);
            // A suspect row with no driver can only be a Credit, so pre-select it. This also
            // satisfies the confirm gating below, which requires every suspect row to be classified.
            const noDriverDefaults = {};
            (preview.suspectRows || []).forEach((r) => {
                if (!r.driverName) noDriverDefaults[r.rowNumber] = 'Credit';
            });
            if (Object.keys(noDriverDefaults).length > 0) {
                setSuspectDecisions(prev => ({ ...noDriverDefaults, ...prev }));
            }
            setConfirmOpen(true);
        } catch (error) {
            setUploadIssues({ fatal: error.message || "Upload validation failed" });
        } finally {
            setUploading(false);
        }
    };

    const confirmUpload = async () => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('approvals', JSON.stringify(
            Object.entries(suspectDecisions).map(([rowNumber, classification]) => ({
                rowNumber: Number(rowNumber),
                classification
            }))
        ));
        formData.append('driverSelections', JSON.stringify(
            Object.entries(driverSelections)
                .filter(([, driverId]) => driverId)
                .map(([driverName, driverId]) => ({
                    driverName,
                    driverId: Number(driverId)
                }))
        ));

        try {
            setUploading(true);
            setConfirmOpen(false);
            const token = await Session.getToken();
            const res = await fetch(routesUploadApi, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            if (!res.ok) {
                if (res.status === 400) {
                    const preview = await res.json();
                    setUploadIssues(preview);
                    return;
                }
                throw new Error(await res.text() || "Upload failed");
            }
            const data = await res.json();
            setPendingPreview(null);
            for (var i = 0; i < weeks.length; i++) {
                const week = weeks[i];
                if (week.routeDays.includes(data.selectedDay)) {
                    setSelectedWeek(week.endingDate);
                    setRouteDays(week.routeDays);
                    setSelectedDay(data.selectedDay);
                    fetchScheduleForDate(data.selectedDay);
                    break;
                }
            }
        } catch (error) {
            setUploadIssues({ fatal: error.message || "Upload failed" });
        } finally {
            setUploading(false);
        }
    };

    const cancelUpload = () => {
        setConfirmOpen(false);
        setPendingPreview(null);
        setSuspectDecisions({});
        setDriverSelections({});
    };

    const formatAmPm = (time) => {
        if (!time) return "";
        const [h, m] = time.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return time;
        const period = h < 12 ? "AM" : "PM";
        const hour = h % 12 || 12;
        return `${hour}:${String(m).padStart(2, '0')} ${period}`;
    };

    const calcHours = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;
        const parse = (t) => {
            const parts = String(t).split(':');
            if (parts.length < 2) return null;
            const h = Number(parts[0]);
            const m = Number(parts[1]);
            if (isNaN(h) || isNaN(m)) return null;
            return h + m / 60;
        };
        const s = parse(startTime);
        let e = parse(endTime);
        if (s === null || e === null) return 0;
        if (e === s) return 0;
        // An end time earlier than the start time means the shift ran past midnight into
        // the next day (e.g. PM start, AM end). Roll the end forward 24h so the elapsed
        // hours are correct. Mirrors OvertimeCalculator.GetWorkedHours on the backend.
        if (e < s) e += 24;
        return e - s;
    };

    // Numeric additional drive time (hours) auto-calculated from worked hours over the route's
    // threshold. Mirrors OvertimeCalculator: deducts 30 min for lunch when the route is
    // configured to, and rounds down to the half hour when the route rounds ADT.
    const calcAdtHours = (startTime, endTime, route) => {
        if (!route?.allowOvertime) return 0;
        const cutoff = route.billingType === "Half-day"
            ? Number(route.halfDayOT ?? 0)
            : Number(route.fullDayOT ?? 0);
        if (cutoff <= 0) return 0;
        let total = calcHours(startTime, endTime);
        if (route.deductLunch && total > 0) total = Math.max(0, total - 0.5);
        if (total <= cutoff) return 0;
        const rawOt = total - cutoff;
        return route.roundOvertimeToHalfHour ? Math.floor(rawOt / 0.5) * 0.5 : rawOt;
    };

    // Effective payable ADT (hours) honoring the manager override and the authorization rule:
    // an override wins; otherwise routes that require authorization pay 0 until authorized.
    const effectiveAdtHours = (rs) => {
        const override = rs.overtimeHoursOverride;
        if (override !== null && override !== undefined && override !== "") return Number(override);
        if (rs.route?.adtRequiresAuthorization) return 0;
        return calcAdtHours(rs.startTime, rs.endTime, rs.route);
    };

    // Format ADT hours as "N × ½hr" when the route rounds to the half hour, else a decimal.
    const formatAdt = (hours, route) => {
        if (!hours || hours <= 0) return "0";
        if (route?.roundOvertimeToHalfHour) {
            return `${Math.round(hours * 2)} × ½hr`;
        }
        return Number(hours).toFixed(2);
    };



    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Manage Daily Routes</h5></Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    setSelectedWeek(value);
                                    saveFilter("selectedWeek", value);
                                    let selected = weeks.find(w => w.endingDate === value);
                                    if (selected)
                                        setRouteDays(selected.routeDays);
                                    let currentDay = dayjs().format('MM/DD/YYYY');
                                    if (selected.routeDays.includes(currentDay)) {
                                        setSelectedDay(currentDay);
                                        saveFilter("selectedDay", currentDay);
                                    } else {
                                        currentDay = selected.routeDays[0];
                                        setSelectedDay(currentDay);
                                        saveFilter("selectedDay", currentDay);
                                    }
                                    fetchScheduleForDate(currentDay);
                                }}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>
                                        {w.displayDate}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col>
                            <Input
                                type="select"
                                value={selectedDay}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    setSelectedDay(value);
                                    saveFilter("selectedDay", value);
                                    fetchScheduleForDate(value);
                                }}
                            >
                                <option value="">-- Select Day --</option>
                                {routeDays.map(d => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        {isAdmin && (
                            <Col>
                                <SearchableDropdownFilter
                                    value={customerId}
                                    options={customers}
                                    placeholder="Select Customer..."
                                    getOptionValue={getOptionId}
                                    getOptionLabel={getCustomerLabel}
                                    onChange={(value) => {
                                        setCustomerId(value);
                                        setSubCustomerId("");
                                        saveFilter("customerId", value);
                                        saveFilter("subCustomerId", "");
                                        clearRouteData();
                                    }}
                                />
                            </Col>
                        )}
                        <Col>
                            <SearchableDropdownFilter
                                value={subCustomerId}
                                options={availableSubCustomers}
                                placeholder="Select Sub-Customer..."
                                getOptionValue={getOptionId}
                                getOptionLabel={getSubCustomerLabel}
                                onChange={(value) => {
                                    setSubCustomerId(value);
                                    saveFilter("subCustomerId", value);
                                    if (isAdmin) fetchScheduleForDate(selectedDay, customerId, value);
                                }}
                                disabled={isAdmin && !customerId}
                            />
                        </Col>
                        <Col>
                            <Input
                                id="subCustomerId"
                                name="subCustomerId"
                                type="file"
                                onChange={
                                    handleFileChange
                                    }
                            >
                            </Input>
                            {uploading && (
                                <div className="text-center"><Spinner /></div>
                            )}
                            {!uploading && (
                                <Button
                                    color="primary"
                                    onClick={handleUpload}
                                >
                                    Upload
                                </Button>
                            ) }
                        </Col>
                    </Row>
                    {uploadIssues && (
                        <Alert color="danger" toggle={() => setUploadIssues(null)}>
                            <div className="fw-bold mb-2">Upload blocked — fix these issues and try again:</div>
                            {uploadIssues.fatal && (<div>{uploadIssues.fatal}</div>)}
                            {uploadIssues.unmatchedRoutes && uploadIssues.unmatchedRoutes.length > 0 && (
                                <div className="mb-2">
                                    <div>Routes not found in database:</div>
                                    <ul className="mb-0">
                                        {uploadIssues.unmatchedRoutes.map(r => (
                                            <li key={`ur-${r.name}`}>
                                                <strong>{r.name}</strong>{r.rowNumbers && r.rowNumbers.length > 0 ? ` — row(s) ${r.rowNumbers.join(', ')}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {uploadIssues.unmatchedDrivers && uploadIssues.unmatchedDrivers.length > 0 && (
                                <div className="mb-2">
                                    <div>Drivers not found in database:</div>
                                    <ul className="mb-0">
                                        {uploadIssues.unmatchedDrivers.map(d => (
                                            <li key={`ud-${d.name}`}>
                                                <strong>{d.name}</strong>{d.rowNumbers && d.rowNumbers.length > 0 ? ` — row(s) ${d.rowNumbers.join(', ')}` : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {uploadIssues.malformedRows && uploadIssues.malformedRows.length > 0 && (
                                <div className="mb-2">
                                    <div>Malformed rows:</div>
                                    <ul className="mb-0">
                                        {uploadIssues.malformedRows.map((m, idx) => (<li key={`mf-${idx}`}>{m}</li>))}
                                    </ul>
                                </div>
                            )}
                        </Alert>
                    )}
                    {/* NEW: Add Route button */}
                    {/*<Button color="primary" onClick={openAddRoute}>+ New Route</Button>*/}
                    {loading ? (
                        <div className="py-5 text-center"><Spinner /></div>
                    ) : error ? (
                        <Alert color="danger">{error}</Alert>
                    ) : isAdmin && (!customerId || !subCustomerId) ? (
                        <Alert color="info">Select a customer and subcustomer to load daily billing routes.</Alert>
                    ) : (
                        <>
                            {/* Schedule summary */}
                            {schedule && (
                                <Row className="mb-4">
                                    <Col md={4}>
                                        <Card>
                                            <CardBody>
                                                <div className="text-muted">Payment Week</div>
                                                <div className="h5 mb-0">{fmt(schedule.startDate)} - {fmt(schedule.endDate)}</div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={4}>
                                        <Card>
                                            <CardBody>
                                                <div className="text-muted">Check Date</div>
                                                <div className="h5 mb-0">{fmt(schedule.payDate)}</div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            {/* RouteSchedules table */}
                            <Table bordered hover responsive>
                                <thead className="table-light">
                                    <tr>
                                        <th>Location</th>
                                        <th>Route</th>
                                        <th style={{ minWidth: 200 }}>Driver</th>
                                        <th style={{ minWidth: 100 }}>Days On Route</th>
                                        <th style={{ minWidth: 100 }}>Mileage</th>
                                        <th>Start</th>
                                        <th>End</th>
                                        <th>Total Hours</th>
                                        {WT_ADT_ENABLED && <th title="Additional Drive Time">ADT</th>}
                                        <th>Total Days</th>
                                        <th>Expected Days</th>
                                        <th>Load</th>
                                        <th style={{ width: 80 }}>{WT_ADT_ENABLED ? "Notes" : "Paysheet Notes"}</th>
                                        {!WT_ADT_ENABLED && <th style={{ width: 80 }}>Invoicing Notes</th>}
                                        <th style={{ width: 60 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grouped.length === 0 ? (
                                        <tr><td colSpan={14} className="text-center">No route schedules</td></tr>
                                    ) : (
                                        grouped.flatMap(({ parent, children }) => {
                                            const expected = 1;
                                            const total = sumDays(parent, children);
                                            const mismatch = Number(total) !== Number(expected);
                                            const makeRow = (rs, isParent) => {
                                                const prevDays = rs.daysOnRoute ?? 0;
                                                const displayVal = typeof rs.daysOnRoute === "number" ? rs.daysOnRoute : (rs.daysOnRoute || "");

                                                const prevMileage = rs.mileage ?? 0;
                                                const displayMileage = typeof rs.mileage === "number" ? rs.mileage : (rs.mileage || "");

                                                const prevStart = rs.startTime ?? "";
                                                const displayStart = typeof rs.startTime === "string" ? rs.startTime : (rs.startTime || "");

                                                const prevEnd = rs.endTime ?? "";
                                                const displayEnd = typeof rs.endTime === "string" ? rs.endTime : (rs.endTime || "");

                                                const prevAdt = (rs.overtimeHoursOverride ?? null);

                                                const prevDriver = rs.driverId;
                                                const isLoadingDevice = !!loadingDeviceRows[rs.id];
                                                return (
                                                    <tr key={rs.id}>
                                                        <td className={!isParent ? 'bg-light' : ''}>{isParent && (rs.route?.subCustomer?.designation ?? "")}</td>
                                                        <td className={!isParent ? 'bg-light' : ''}>{isParent && (rs.route?.name ?? "")}</td>
                                                        <td>
                                                            <Input
                                                                type="select"
                                                                value={rs.driverId || ""}
                                                                onChange={(e) => handleDriverChange(rs.id, e, prevDriver)}
                                                            >
                                                                <option value="">-- Select Driver --</option>
                                                                {drivers.filter(d => driverMatchesState(d, rs.route?.subCustomer?.state)).map(d => (
                                                                    <option key={d.id} value={d.id}>
                                                                        {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                max="1"
                                                                step="0.01"
                                                                value={displayVal}
                                                                onChange={(e) => handleDays(rs.id, e, prevDays)}
                                                                onBlur={(e) => handleDays(rs.id, e, prevDays)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" || e.key === "Escape") handleDays(rs.id, e, prevDays);
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                value={displayMileage}
                                                                onChange={(e) => handleMileage(rs.id, e, prevMileage)}
                                                                onBlur={(e) => handleMileage(rs.id, e, prevMileage)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" || e.key === "Escape") handleMileage(rs.id, e, prevMileage);
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="time"
                                                                value={displayStart}
                                                                onChange={(e) => handleStart(rs.id, e, prevStart)}
                                                                onBlur={(e) => handleStart(rs.id, e, prevStart)}
                                                                //onKeyDown={(e) => handleStart(rs.id, e, prevStart)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="time"
                                                                value={displayEnd}
                                                                onChange={(e) => handleEnd(rs.id, e, prevEnd)}
                                                                onBlur={(e) => handleEnd(rs.id, e, prevEnd)}
                                                                //onKeyDown={(e) => handleEnd(rs.id, e, prevEnd)}
                                                            />
                                                        </td>
                                                        <td>
                                                            {calcHours(displayStart, displayEnd).toFixed(2)}
                                                        </td>
                                                        {WT_ADT_ENABLED && (
                                                        <td style={{ textAlign: 'center', minWidth: 120 }}>
                                                            {rs.route?.allowOvertime ? (
                                                                <>
                                                                    <Input
                                                                        type="number"
                                                                        bsSize="sm"
                                                                        min="0"
                                                                        step={rs.route?.roundOvertimeToHalfHour ? "0.5" : "any"}
                                                                        value={rs.overtimeHoursOverride ?? ""}
                                                                        placeholder={Number(calcAdtHours(displayStart, displayEnd, rs.route)).toFixed(2)}
                                                                        title={rs.route?.roundOvertimeToHalfHour
                                                                            ? "Payable additional drive time in hours (blank = auto). 0.5 = one ½hr."
                                                                            : "Payable additional drive time in hours (blank = auto)."}
                                                                        onChange={(e) => handleAdt(rs.id, e, prevAdt)}
                                                                        onBlur={(e) => handleAdt(rs.id, e, prevAdt)}
                                                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") handleAdt(rs.id, e, prevAdt); }}
                                                                    />
                                                                    <div className="text-muted small mt-1">
                                                                        pay: {formatAdt(effectiveAdtHours({ ...rs, startTime: displayStart, endTime: displayEnd }), rs.route)}
                                                                    </div>
                                                                </>
                                                            ) : ""}
                                                        </td>
                                                        )}
                                                        <td className={isParent && mismatch ? "table-danger fw-bold" : (isParent ? "fw-bold" : "")}>
                                                            {isParent ? total : ""}
                                                        </td>
                                                        <td className={isParent ? "fw-bold" : ""}>
                                                            {isParent ? expected : ""}
                                                        </td>
                                                        <td className="text-end">
                                                            <Button
                                                                size="sm"
                                                                color="success"
                                                                onClick={() => loadDataFromDevice(rs, rs.routeId)}
                                                                disabled={isLoadingDevice}
                                                            >
                                                                {isLoadingDevice ? (
                                                                    <>
                                                                        <Spinner size="sm" className="me-1" />
                                                                        Loading
                                                                    </>
                                                                ) : "Load"}
                                                            </Button>
                                                        </td>
                                                        <td className="text-center">
                                                            <Button
                                                                size="sm"
                                                                color={rs.comments ? "info" : "secondary"}
                                                                outline={!rs.comments}
                                                                title={rs.comments || "Add notes"}
                                                                onClick={() => openNotes(rs)}
                                                            >
                                                                Notes
                                                            </Button>
                                                        </td>
                                                        {!WT_ADT_ENABLED && (
                                                        <td className="text-center">
                                                            <Button
                                                                size="sm"
                                                                color={rs.invoicingComments ? "info" : "secondary"}
                                                                outline={!rs.invoicingComments}
                                                                title={rs.invoicingComments || "Add invoicing notes"}
                                                                onClick={() => openInvNotes(rs)}
                                                            >
                                                                Notes
                                                            </Button>
                                                        </td>
                                                        )}
                                                        <td className="text-end">
                                                            {isParent ? (
                                                                <Button size="sm" color="success" onClick={() => openAdd(rs.routeId, rs.route?.subCustomer?.state)}>+</Button>
                                                            ) : (
                                                                <Button size="sm" color="danger" onClick={() => deleteSplitRow(rs.id)}>Remove</Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            };
                                            return [makeRow(parent, true), ...children.map(c => makeRow(c, false))];
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </>
                    )}
                </CardBody>
            </Card>
            {/* Spireon Preview Modal */}
            <Modal isOpen={spireonPreviewOpen} toggle={cancelSpireonPreview} size="lg">
                <ModalHeader toggle={cancelSpireonPreview}>Review Spireon Splits</ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <div className="fw-bold">{spireonPreviewRoute?.route?.name ?? "Route"} - {selectedDay}</div>
                        <div className="text-muted">Choose the GPS split rows to apply before any daily billing rows are created.</div>
                    </div>
                    {spireonPreviewSegments.some(s => s.isPotentiallyInvalid) && (
                        <Alert color="warning" className="mb-2 py-2">
                            <strong>One or more segments are flagged as potentially invalid.</strong> These have been excluded from billing by default. Review the Status column before applying.
                        </Alert>
                    )}
                    {spireonPreviewSegments.some(s => s.canMergeWithNext) && (
                        <Alert color="info" className="mb-2 py-2">
                            GPS data contains segments split on unauthorized movement with no confirmed vehicle or driver swap. These are pre-selected for merging — uncheck below to keep them as separate billing rows.
                        </Alert>
                    )}
                    <Table bordered responsive size="sm">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: 40 }}>Use</th>
                                <th>Split</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Mileage</th>
                                <th>Start Odo</th>
                                <th>End Odo</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {spireonPreviewSegments.map(segment => (
                                <tr
                                    key={`spireon-${segment.segmentIndex}`}
                                    className={segment.isPotentiallyInvalid ? "table-warning" : ""}
                                >
                                    <td>
                                        <Input
                                            type="checkbox"
                                            checked={!!segment.selected}
                                            onChange={(e) => updateSpireonSegmentSelection(segment.segmentIndex, e.target.checked)}
                                        />
                                    </td>
                                    <td>{(segment.segmentIndex ?? 0) + 1}</td>
                                    <td>{formatAmPm(segment.startTime)}</td>
                                    <td>{formatAmPm(segment.endTime)}</td>
                                    <td>{Math.round(Number(segment.mileage) || 0)}</td>
                                    <td>{segment.startMileage ?? ""}</td>
                                    <td>{segment.endMileage ?? ""}</td>
                                    <td>
                                        {segment.isPotentiallyInvalid && (
                                            <span
                                                className="badge bg-warning text-dark me-1"
                                                title={segment.invalidReason || "Potentially invalid"}
                                                style={{ cursor: "help" }}
                                            >
                                                ⚠ Invalid
                                            </span>
                                        )}
                                        {segment.canMergeWithNext && (
                                            <span className="badge bg-secondary" title="This segment and the next can be merged">
                                                ⇌ Merge candidate
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <FormGroup check className="mb-3">
                        <Input
                            id="mergeSpireonSplits"
                            type="checkbox"
                            checked={mergeSpireonSplits}
                            disabled={getSelectedSpireonSegments().length < 2}
                            onChange={(e) => setMergeSpireonSplits(e.target.checked)}
                        />
                        <Label check for="mergeSpireonSplits">
                            Merge selected splits into one daily billing row
                        </Label>
                    </FormGroup>
                    {mergeSpireonSplits && getPendingSpireonRows().length > 0 && (
                        <Alert color="info">
                            Merged row preview: {formatAmPm(getPendingSpireonRows()[0].startTime) || "no start"} to {formatAmPm(getPendingSpireonRows()[0].endTime) || "no end"}, {Math.round(Number(getPendingSpireonRows()[0].mileage) || 0)} miles.
                        </Alert>
                    )}
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={cancelSpireonPreview}>Cancel</Button>
                        <Button color="primary" onClick={applySpireonPreview} disabled={getPendingSpireonRows().length === 0}>Apply Selected Data</Button>
                    </div>
                </ModalBody>
            </Modal>
            {/* Add Child Modal */}
            <Modal isOpen={addOpen} toggle={() => setAddOpen(false)}>
                <ModalHeader toggle={() => setAddOpen(false)}>Add Driver to Route</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="driverSel">Driver</Label>
                        <Input id="driverSel" type="select" value={newDriverId} onChange={(e) => setNewDriverId(e.target.value)}>
                            <option value="">-- Select Driver --</option>
                            {drivers.filter(d => driverMatchesState(d, addForState)).map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup>
                        <Label for="daysInput">Days On Route</Label>
                        <Input id="daysInput" type="number" min="0" step="0.1" value={newDays} onChange={(e) => setNewDays(e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                        <Label for="mileageInput">Mileage</Label>
                        <Input id="mileageInput" type="number" min="0" step="0.1" value={newMileage} onChange={(e) => setNewMileage(e.target.value)} />
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
                        <Button color="primary" onClick={submitAddChild} disabled={!newDriverId}>Add</Button>
                    </div>
                </ModalBody>
            </Modal>
            {/* Upload Confirmation Modal */}
            <Modal isOpen={confirmOpen} toggle={cancelUpload}>
                <ModalHeader toggle={cancelUpload}>Confirm Upload</ModalHeader>
                <ModalBody>
                    {pendingPreview && (
                        <>
                            <p>This will import <strong>{pendingPreview.rowCount}</strong> row(s).</p>
                            <div className="mb-2">
                                <div className="fw-bold">Dates:</div>
                                <div>{pendingPreview.affectedDates?.join(', ')}</div>
                            </div>
                            <div className="mb-2">
                                <div className="fw-bold">Routes:</div>
                                <div>{pendingPreview.affectedRoutes?.join(', ')}</div>
                            </div>
                            {pendingPreview.warnings?.length > 0 && (
                                <Alert color="warning" className="mt-3">
                                    <div className="fw-bold mb-1">Mileage warnings</div>
                                    <p className="mb-2">
                                        The odometer-computed mileage for the following row(s) does not match the
                                        spreadsheet's Total Miles column, which usually means a stale or broken formula
                                        in the sheet. The odometer value (end − start) will be imported. Review the source
                                        file if these look wrong.
                                    </p>
                                    <ul className="mb-0">
                                        {pendingPreview.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}
                            {pendingPreview.ambiguousDrivers?.length > 0 && (
                                <Alert color="warning" className="mt-3">
                                    <div className="fw-bold mb-1">Drivers needing selection</div>
                                    <p className="mb-2">
                                        The following driver name(s) match more than one driver in the
                                        database. Select which driver to use for each before importing.
                                    </p>
                                    {pendingPreview.ambiguousDrivers.map((d) => (
                                        <div key={d.name} className="border rounded p-2 mb-2 bg-white">
                                            <div>
                                                <strong>{d.name}</strong>
                                                {d.rowNumbers?.length > 0 ? ` — row(s) ${d.rowNumbers.join(', ')}` : ''}
                                            </div>
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                className="mt-2"
                                                value={driverSelections[d.name] || ''}
                                                onChange={(e) => setDriverSelections(prev => ({ ...prev, [d.name]: e.target.value }))}
                                            >
                                                <option value="" disabled>-- select a driver --</option>
                                                {d.options?.map((o) => (
                                                    <option key={o.id} value={o.id}>
                                                        {o.driverName}
                                                        {o.businessName ? ` — ${o.businessName}` : ''}
                                                        {o.state ? ` (${o.state})` : ''}
                                                        {o.active === false ? ' — Inactive' : ''}
                                                    </option>
                                                ))}
                                            </Input>
                                        </div>
                                    ))}
                                </Alert>
                            )}
                            {pendingPreview.suspectRows?.length > 0 && (
                                <Alert color="warning" className="mt-3">
                                    <div className="fw-bold mb-1">Rows needing review</div>
                                    <p className="mb-2">
                                        The following row(s) are missing time and/or mileage values, or have an invoicing note
                                        mentioning a <strong>false trip</strong>, and may represent a
                                        {' '}<strong>false trip</strong> or a <strong>credit</strong>. Classify each row to approve
                                        it for import. Rows classified as Normal import as regular entries.
                                    </p>
                                    {pendingPreview.suspectRows.map((r) => {
                                        // A row with no driver can only be a Credit (a zero-day run,
                                        // not billed and not paid); restrict its options accordingly.
                                        const noDriver = !r.driverName;
                                        return (
                                        <div key={r.rowNumber} className="border rounded p-2 mb-2 bg-white">
                                            <div>
                                                <strong>Row {r.rowNumber}</strong> — {r.routeDate} · {r.driverName || '(no driver)'} · {r.routeName}
                                            </div>
                                            {r.missingFields?.length > 0 && (
                                                <div className="text-danger small">Missing: {r.missingFields.join(', ')}</div>
                                            )}
                                            {r.invoicingComments && (
                                                <div className="small mt-1">
                                                    <span className="fw-bold">Invoicing note:</span> {r.invoicingComments}
                                                </div>
                                            )}
                                            {r.comments && (
                                                <div className="small mt-1">
                                                    <span className="fw-bold">Note:</span> {r.comments}
                                                </div>
                                            )}
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                className="mt-2"
                                                value={suspectDecisions[r.rowNumber] || (noDriver ? 'Credit' : '')}
                                                onChange={(e) => setSuspectDecisions(prev => ({ ...prev, [r.rowNumber]: e.target.value }))}
                                            >
                                                <option value="" disabled>-- classify to approve --</option>
                                                {!noDriver && <option value="Normal">Normal</option>}
                                                {!noDriver && <option value="FalseTrip">False Trip</option>}
                                                <option value="Credit">Credit</option>
                                            </Input>
                                        </div>
                                        );
                                    })}
                                </Alert>
                            )}
                            <Alert color="warning" className="mt-3 mb-0">
                                <strong>Warning:</strong> Existing entries for these date/route combinations will be cleared before the new rows are inserted. This cannot be undone.
                            </Alert>
                        </>
                    )}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button color="secondary" onClick={cancelUpload}>Cancel</Button>
                        <Button
                            color="primary"
                            onClick={confirmUpload}
                            disabled={
                                (pendingPreview?.suspectRows || []).some(r => !suspectDecisions[r.rowNumber])
                                || (pendingPreview?.ambiguousDrivers || []).some(d => !driverSelections[d.name])
                            }
                        >
                            Confirm Upload
                        </Button>
                    </div>
                    {(pendingPreview?.suspectRows || []).some(r => !suspectDecisions[r.rowNumber]) && (
                        <div className="text-muted small text-end mt-1">
                            Classify all rows needing review to enable the upload.
                        </div>
                    )}
                    {(pendingPreview?.ambiguousDrivers || []).some(d => !driverSelections[d.name]) && (
                        <div className="text-muted small text-end mt-1">
                            Select a driver for each ambiguous name to enable the upload.
                        </div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={notesOpen} toggle={() => !notesSaving && setNotesOpen(false)}>
                <ModalHeader toggle={() => !notesSaving && setNotesOpen(false)}>Notes</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="dailyNotesInput">Comments for this daily row</Label>
                        <Input
                            id="dailyNotesInput"
                            type="textarea"
                            rows={6}
                            maxLength={2000}
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            disabled={notesSaving}
                        />
                        <div className="text-muted small mt-1">{notesText.length} / 2000</div>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setNotesOpen(false)} disabled={notesSaving}>Cancel</Button>
                        <Button color="primary" onClick={saveNotes} disabled={notesSaving}>
                            {notesSaving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={invNotesOpen} toggle={() => !invNotesSaving && setInvNotesOpen(false)}>
                <ModalHeader toggle={() => !invNotesSaving && setInvNotesOpen(false)}>Invoicing Notes</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="dailyInvNotesInput">Invoicing notes for this daily row (impacts the customer invoice)</Label>
                        <Input
                            id="dailyInvNotesInput"
                            type="textarea"
                            rows={6}
                            maxLength={2000}
                            value={invNotesText}
                            onChange={(e) => setInvNotesText(e.target.value)}
                            disabled={invNotesSaving}
                        />
                        <div className="text-muted small mt-1">{invNotesText.length} / 2000</div>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setInvNotesOpen(false)} disabled={invNotesSaving}>Cancel</Button>
                        <Button color="primary" onClick={saveInvNotes} disabled={invNotesSaving}>
                            {invNotesSaving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ManagerDailyRoutes;
